// LaunchScreen — redesign Phase 8a, "launch screen animation".
//
// Built to docs/designs/OnbLaunch.dc.html (ground truth for colors/sizing/
// keyframe timing). Sunrise + logo-up animation, then the verse fades in
// line by line, then the reference in small-caps gold ink. Auto-advances
// (no tap) after the sequence completes.
//
// Scope note: the spec also describes substituting a church-specific verse
// (the key passage from that church's latest sermon) when reached via an
// invite link. That needs the app's Bible-text dataset to resolve an
// arbitrary reference to its actual wording, which this component doesn't
// have access to — deferred as a fast-follow; this always shows the
// Lamentations fallback, which the spec itself sanctions as the no-church
// branch.
//
// Props:
//   variant  — 'full' (verse sequence, ~3s) | 'brief' (logo only, ~0.6s),
//              chosen by the caller based on whether a session was already
//              restored at mount (spec: returning signed-in users only get
//              the brief logo moment).
//   onDone() — called once the sequence completes; caller unmounts this.

import { useEffect, useState } from "react";

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
  catch { return false; }
}

export default function LaunchScreen({ variant = "full", onDone }) {
  const [reduceMotion] = useState(prefersReducedMotion);

  useEffect(() => {
    const ms = variant === "brief" ? 600 : (reduceMotion ? 2000 : 3000);
    const t = setTimeout(() => onDone?.(), ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const animate = !reduceMotion;
  const showVerse = variant === "full";

  return (
    <div style={S.wrap}>
      <style>{CSS}</style>
      <svg
        className={animate ? "tbc-logo" : undefined}
        style={reduceMotion ? S.logoStatic : S.logo}
        viewBox="0 0 100 100" aria-hidden="true"
      >
        <circle className={animate ? "tbc-sun" : undefined} cx="50" cy="62" r="19" fill="#E8C87A"/>
        <g className={animate ? "tbc-rays" : undefined} stroke="#E8C87A" strokeWidth="3.5" strokeLinecap="round">
          <line x1="50" y1="24" x2="50" y2="14"/>
          <line x1="72" y1="34" x2="80" y2="26"/>
          <line x1="28" y1="34" x2="20" y2="26"/>
          <line x1="78" y1="52" x2="88" y2="52"/>
          <line x1="22" y1="52" x2="12" y2="52"/>
        </g>
        <path d="M50 58 L50 92 L18 92 Q14 92 14 86 L14 64 Q14 58 20 58 Z" fill="#1F1B16"/>
        <path d="M50 58 L50 92 L82 92 Q86 92 86 86 L86 64 Q86 58 80 58 Z" fill="#3A332C"/>
      </svg>

      {showVerse && (
        <div style={S.verseWrap}>
          <div className={animate ? "tbc-l1" : undefined} style={S.verseLine}>His mercies</div>
          <div className={animate ? "tbc-l2" : undefined} style={S.verseLine}>are new</div>
          <div className={animate ? "tbc-l3" : undefined} style={{ ...S.verseLine, marginBottom: 14 }}>every morning</div>
          <div className={animate ? "tbc-l4" : undefined} style={S.ref}>LAMENTATIONS 3:22–23</div>
        </div>
      )}
    </div>
  );
}

const CSS = `
@keyframes tbc-sunrise { 0%{transform:translateY(22px);opacity:0} 25%{transform:translateY(0);opacity:1} 100%{transform:translateY(0);opacity:1} }
@keyframes tbc-rays-fade { 0%,20%{opacity:0} 35%,100%{opacity:1} }
@keyframes tbc-logo-up { 0%,38%{transform:translateY(0) scale(1)} 50%,100%{transform:translateY(-190px) scale(0.55)} }
@keyframes tbc-line-fade { 0%{opacity:0;transform:translateY(8px)} 100%{opacity:1;transform:translateY(0)} }
.tbc-sun{animation:tbc-sunrise 3s ease-out forwards}
.tbc-rays{animation:tbc-rays-fade 3s ease-out forwards}
.tbc-logo{animation:tbc-logo-up 3s ease-in-out forwards;transform-origin:center}
.tbc-l1{animation:tbc-line-fade 3s ease-out forwards;animation-timing-function:steps(1,end);animation-delay:1.5s;opacity:0}
.tbc-l2{animation:tbc-line-fade 3s ease-out forwards;animation-delay:1.74s;opacity:0}
.tbc-l3{animation:tbc-line-fade 3s ease-out forwards;animation-delay:1.98s;opacity:0}
.tbc-l4{animation:tbc-line-fade 3s ease-out forwards;animation-delay:2.28s;opacity:0}
`;

const S = {
  wrap: { position: "fixed", inset: 0, zIndex: 2000, background: "#F6F2EC", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },
  logo: { width: 150, height: 150, flexShrink: 0 },
  logoStatic: { width: 82.5, height: 82.5, flexShrink: 0, marginBottom: 24 },
  verseWrap: { marginTop: 36, textAlign: "center" },
  verseLine: { fontFamily: "'Source Serif 4',Georgia,serif", fontSize: 27, fontWeight: 600, lineHeight: 1.3, color: "#1F1B16" },
  ref: { fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 12.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7A5A12" },
};
